<script lang="ts" setup>
const { t } = useI18n();
const localePath = useLocalePath();

const route = useRoute();


const company = computed(() => [
  {
    name: t("spendzen.menu.features"),
    to: { path: localePath("/"), hash: "#features" },
  },
  {
    name: t("spendzen.menu.pricing"),
    to: { path: localePath("/"), hash: "#pricing" },
  },
  {
    name: t("footer.contact"),
    to: { path: localePath("/"), hash: "#contact-section" },
  },
]);

const legal = [
  { name: t("footer.cgv"), path: "/cgv" },
  { name: t("footer.cgu"), path: "/cgu" },
  { name: t("footer.privacy"), path: "/privacy" },
];
</script>

<template>
  <footer class="relative z-50 bg-gray-50 dark:bg-gray-950 pt-10 pb-10 mt-0">
    <!-- Background Decor (Subtle) -->
    <div
      aria-hidden="true"
      class="absolute top-0 right-0 w-1/3 h-full opacity-10 dark:opacity-20 pointer-events-none"
    >
      <div
        class="blur-[120px] h-full bg-gradient-to-l from-primary/30 to-transparent"
      ></div>
    </div>

    <Container>
      <div
        class="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-20"
      >
        <!-- Logo & Description -->
        <div class="col-span-1 md:col-span-2 lg:col-span-1 space-y-8">
          <NuxtLink
            :to="localePath('/')"
            class="inline-block transition-opacity hover:opacity-80"
          >
            <AppIcon width="180" class="h-auto" />
          </NuxtLink>
          <p
            class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed max-w-sm"
          >
            {{ t("footer.tagline") }}
          </p>
          <div class="flex items-center gap-5">
            <a
              href="https://linkedin.com/company/xpirience"
              target="_blank"
              rel="noopener noreferrer"
              class="group flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm transition-all hover:border-primary hover:text-primary"
            >
              <span class="sr-only">LinkedIn</span>
              <svg
                class="w-5 h-5 fill-current"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </a>
          </div>
        </div>

        <!-- Links Groups -->
        <div class="space-y-8">
          <h3
            class="text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white"
          >
            {{ t("header.solutions") }}
          </h3>
          <ul class="space-y-4">
            <li>
              <NuxtLink
                :to="localePath('/')"
                class="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors text-sm font-medium"
              >
                SpendZen
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div class="space-y-8">
          <h3
            class="text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white"
          >
            {{ t("footer.company") }}
          </h3>
          <ul class="space-y-4">
            <li v-for="item in company" :key="item.name">
              <NuxtLink
                :to="item.to"
                class="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors text-sm font-medium"
              >
                {{ item.name }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- Contact & Address -->
        <div class="space-y-8">
          <h3
            class="text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white"
          >
            {{ t("footer.contact") }}
          </h3>
          <div class="space-y-5 text-sm">
            <div class="flex items-start gap-4">
              <div
                class="flex items-center justify-center w-8 h-8 rounded bg-primary/10 text-primary shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                  />
                </svg>
              </div>
              <p class="text-gray-600 dark:text-gray-400 leading-relaxed pt-1">
                {{ t("footer.address") }}
              </p>
            </div>
            <div class="flex items-center gap-4">
              <div
                class="flex items-center justify-center w-8 h-8 rounded bg-primary/10 text-primary shrink-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
              </div>
              <a
                href="mailto:info@xpirience.ch"
                class="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors"
              >
                info@xpirience.ch
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div
        class="relative z-10 pt-10 border-t border-gray-100 dark:border-gray-800 flex flex-col md:flex-row justify-between items-center gap-8"
      >
        <div class="flex flex-col items-center md:items-start space-y-2">
          <p class="text-gray-500 dark:text-gray-500 text-xs">
            {{ t("footer.copyright") }}
          </p>
          <i18n-t
            keypath="footer.maintained_by"
            tag="div"
            class="flex items-center gap-1.5 text-gray-400 dark:text-gray-600 text-[10px] uppercase tracking-widest font-medium"
          >
            <template #studio>
              <a
                href="https://ezyostudio.com"
                target="_blank"
                class="hover:text-primary transition-colors duration-300"
              >
                EzyoStudio
              </a>
            </template>
          </i18n-t>
        </div>

        <div class="flex flex-wrap justify-center gap-x-8 gap-y-3">
          <NuxtLink
            v-for="link in legal"
            :key="link.name"
            :to="localePath(link.path)"
            class="text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors text-xs font-semibold uppercase tracking-wider"
          >
            {{ link.name }}
          </NuxtLink>
        </div>
      </div>
    </Container>
  </footer>
</template>
